import { useState, useEffect, useMemo } from 'react'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import {
  AdvancedDateRangePicker,
  ANGULAR_COMPANY_STATS_PRESETS,
} from '@/components/ui/AdvancedDateRangePicker'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import type { ApplicationFilters } from '../types/application'
import type { ApplicationFiltersProps } from '../types/components'
import {
  APPLICATION_STATUS_OPTIONS,
  getProgressStatusOptions,
  EMPLOYER_SEARCH_OPTIONS,
  PAST_CUSTOMER_OPTIONS,
} from '../utils/applicationConstants'
import {
  getProperties,
  getCompanies,
  getActiveProductOptions,
  getActiveModelOptions,
  getApplicationRiskRatingLabels,
} from '../services/applicationService'
import { formatDateForAPI } from '../utils/applicationFormatters'
import { hasPolicy } from '../utils/applicationPolicies'
import { shouldRunTypeaheadSearch } from '../utils/applicationListLogic'
import { showError } from '@/lib/utils/notifications'
import { Filter, X } from 'lucide-react'
import styles from '../styles/applicationList.module.css'

function isFilterValueSelected(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

function filterSelectWrapClass(value: unknown, extraClassName = ''): string {
  const filled = isFilterValueSelected(value)
  return [
    'relative',
    styles.pillFilterWrapperVertical,
    filled ? styles.pillFilterFilled : styles.pillFilterEmpty,
    extraClassName,
  ]
    .filter(Boolean)
    .join(' ')
}

function FilterLabel({
  children,
  field,
}: {
  children: string
  field: string
}) {
  return (
    <label
      data-testid={`application-list-filters-${field}-label`}
      className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
    >
      {children}
    </label>
  )
}

export function ApplicationFilters({
  filters,
  onFiltersChange,
  onReset,
  isOpen,
  onClose,
}: ApplicationFiltersProps) {
  const user = useAppSelector(selectUser)
  const showEmployerSearch = hasPolicy(user, ['application_advance_search'])
  const showProductFilter = hasPolicy(user, ['application_list_product_filter'])
  const showModelFilter = hasPolicy(user, ['application_list_model_filter'])
  const showApplicationScreeningApproachFilter = hasPolicy(user, ['application_list_application_screening_approach_filter'])
  const showApplicantScreeningApproachFilter = hasPolicy(user, ['application_list_applicant_screening_approach_filter'])
  const showApplicationRiskRatingFilter = hasPolicy(user, ['application_list_application_risk_rating_filter'])
  const showApplicantRiskRatingFilter = hasPolicy(user, ['application_list_applicant_risk_rating_filter'])

  // -- Data Loading State --
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)
  const [productOptions, setProductOptions] = useState<SearchableSelectOption[]>([])
  const [modelOptions, setModelOptions] = useState<SearchableSelectOption[]>([])
  const [riskRatingOptions, setRiskRatingOptions] = useState<SearchableSelectOption[]>([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [loadingModels, setLoadingModels] = useState(false)
  const [loadingRiskLabels, setLoadingRiskLabels] = useState(false)
  const [screeningApproachDraft, setScreeningApproachDraft] = useState(
    filters.applicant_screening_approach || '',
  )
  const [applicationScreeningApproachDraft, setApplicationScreeningApproachDraft] = useState(
    filters.application_screening_approach || '',
  )

  // -- Role Locking --
  const lockedCompanyId = useMemo(() => {
    const roleCompanyId = user?.current_role?.company_id
    return roleCompanyId ? String(roleCompanyId) : undefined
  }, [user])

  const lockedPropertyId = useMemo(() => {
    const rolePropertyId = user?.current_role?.property_id
    return rolePropertyId ? String(rolePropertyId) : undefined
  }, [user])

  // Locked company/property labels for read-only select (Angular getAccessiblePermission).
  const lockedCompanyName = user?.current_role?.company_name ?? undefined
  const lockedPropertyName = user?.current_role?.property_name ?? undefined

  // Get dynamic progress status options based on selected applicant statuses
  const progressStatusOptions = useMemo(() => {
    if (!filters.applicantStatus || filters.applicantStatus.length === 0) {
      return []
    }
    return getProgressStatusOptions(filters.applicantStatus as Parameters<typeof getProgressStatusOptions>[0])
  }, [filters.applicantStatus])

  // -- Data Fetchers --
  const loadCompanies = async (search: string) => {
    setLoadingCompanies(true)
    try {
      const data = await getCompanies(search)
      const mapped = data.map((company) => ({
        value: String(company.company_id),
        label: company.company_name || String(company.company_id),
      }))
      setCompanies(mapped)
      // Angular loadCompany: if exactly one result returned, auto-select it (setTimeout 10ms)
      if (mapped.length === 1 && !lockedCompanyId) {
        setTimeout(() => {
          onFiltersChange(
            { company_id: mapped[0].value, property_id: undefined },
            { company_name: mapped[0].label }
          )
          setProperties([])
        }, 10)
      }
    } catch (error) {
      // Angular loadCompany: notify(error.error, 0) on failure
      showError((error as { message?: string })?.message || 'Failed to load companies.')
    } finally {
      setLoadingCompanies(false)
    }
  }

  const loadProperties = async (search: string, companyId?: string | number) => {
    setLoadingProperties(true)
    try {
      const data = await getProperties(search, companyId)
      const mapped = data.map((property) => ({
        value: String(property.property_id),
        label: property.property_name || String(property.property_id),
      }))
      setProperties(mapped)
      // Angular loadProperty: if exactly one result and no search text, auto-select it (setTimeout 10ms)
      if (mapped.length === 1 && !search && !lockedPropertyId) {
        setTimeout(() => {
          onFiltersChange(
            { property_id: mapped[0].value },
            { property_name: mapped[0].label }
          )
        }, 10)
      }
    } catch (error) {
      // Angular loadProperty: notify(error.error, 0) on failure
      showError((error as { message?: string })?.message || 'Failed to load properties.')
    } finally {
      setLoadingProperties(false)
    }
  }

  // Angular searchCompany/searchProperty: fire the type-ahead API only on every 2nd character
  const searchCompany = (search: string) => {
    if (shouldRunTypeaheadSearch(search)) {
      loadCompanies(search)
    }
  }

  const searchProperty = (search: string) => {
    if (shouldRunTypeaheadSearch(search)) {
      loadProperties(search, filters.company_id || lockedCompanyId)
    }
  }

  // Initial Load & Handlers
  useEffect(() => {
    if (!lockedCompanyId && companies.length === 0) {
      loadCompanies('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCompanyId])

  useEffect(() => {
    if (!isOpen || !showProductFilter) return
    let cancelled = false
    void (async () => {
      setLoadingProducts(true)
      try {
        const rows = await getActiveProductOptions()
        if (cancelled) return
        const mapped = rows.map((p) => ({
          value: String(p.product_id),
          label: p.name || String(p.product_id),
        }))
        const ids = filters.product_id || []
        const names = filters.product_name ? String(filters.product_name).split(', ') : []
        const missing = ids
          .map((id, i) => ({
            id: String(id),
            label: names[i]?.trim() || `Product ${id}`,
          }))
          .filter((row) => !mapped.some((m) => String(m.value) === row.id))
        setProductOptions([...mapped, ...missing.map((m) => ({ value: m.id, label: m.label }))])
      } catch {
        if (!cancelled) setProductOptions([])
      } finally {
        if (!cancelled) setLoadingProducts(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, showProductFilter, filters.product_id])

  useEffect(() => {
    if (!isOpen || !showModelFilter) return
    let cancelled = false
    void (async () => {
      setLoadingModels(true)
      try {
        const rows = await getActiveModelOptions()
        if (cancelled) return
        const mapped = rows.map((m) => ({
          value: String(m.model_id),
          label: m.model_name || String(m.model_id),
        }))
        const ids = filters.model_id || []
        const names = filters.model_name ? String(filters.model_name).split(', ') : []
        const missing = ids
          .map((id, i) => ({
            id: String(id),
            label: names[i]?.trim() || `Model ${id}`,
          }))
          .filter((row) => !mapped.some((m) => String(m.value) === row.id))
        setModelOptions([...mapped, ...missing.map((m) => ({ value: m.id, label: m.label }))])
      } catch {
        if (!cancelled) setModelOptions([])
      } finally {
        if (!cancelled) setLoadingModels(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, showModelFilter, filters.model_id])

  useEffect(() => {
    if (!isOpen || (!showApplicationRiskRatingFilter && !showApplicantRiskRatingFilter)) return
    let cancelled = false
    void (async () => {
      setLoadingRiskLabels(true)
      try {
        const labels = await getApplicationRiskRatingLabels()
        if (cancelled) return
        const base = labels.map((t) => ({ value: t, label: t }))
        const selected = [
          ...(filters.application_risk_rating || []),
          ...(filters.applicant_risk_rating || []),
        ]
        const extra = selected
          .filter((r) => !labels.includes(r))
          .map((t) => ({ value: t, label: t }))
        setRiskRatingOptions([...base, ...extra])
      } catch {
        if (!cancelled) setRiskRatingOptions([])
      } finally {
        if (!cancelled) setLoadingRiskLabels(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    showApplicationRiskRatingFilter,
    showApplicantRiskRatingFilter,
    filters.application_risk_rating,
    filters.applicant_risk_rating,
  ])

  useEffect(() => {
    setScreeningApproachDraft(filters.applicant_screening_approach || '')
  }, [filters.applicant_screening_approach])

  useEffect(() => {
    setApplicationScreeningApproachDraft(filters.application_screening_approach || '')
  }, [filters.application_screening_approach])

  useEffect(() => {
    if (!showApplicantScreeningApproachFilter) return
    const timer = setTimeout(() => {
      const trimmed = screeningApproachDraft.trim()
      const current = (filters.applicant_screening_approach || '').trim()
      if (trimmed !== current) {
        onFiltersChange({
          applicant_screening_approach: trimmed || undefined,
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [
    screeningApproachDraft,
    filters.applicant_screening_approach,
    onFiltersChange,
    showApplicantScreeningApproachFilter,
  ])

  useEffect(() => {
    if (!showApplicationScreeningApproachFilter) return
    const timer = setTimeout(() => {
      const trimmed = applicationScreeningApproachDraft.trim()
      const current = (filters.application_screening_approach || '').trim()
      if (trimmed !== current) {
        onFiltersChange({
          application_screening_approach: trimmed || undefined,
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [applicationScreeningApproachDraft, filters.application_screening_approach, onFiltersChange, showApplicationScreeningApproachFilter])

  useEffect(() => {
    if (
      !lockedPropertyId &&
      properties.length === 0 &&
      (filters.company_id || lockedCompanyId)
    ) {
      loadProperties('', filters.company_id || lockedCompanyId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPropertyId, filters.company_id, lockedCompanyId])

  const handleDateChange = (range: { start: Date | null; end: Date | null }) => {
    // Convert Date to MM-DD-YYYY format, then to DD-MM-YYYY for API
    const formatDateToAPI = (date: Date): string => {
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      const mmddyyyy = `${month}-${day}-${year}`
      return formatDateForAPI(mmddyyyy)
    }
    onFiltersChange({
      fromDt: range.start ? formatDateToAPI(range.start) : undefined,
      toDt: range.end ? formatDateToAPI(range.end) : undefined,
    })
  }

  // Handle drawer close logic
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return

    const scrollY = window.scrollY
    const { body, documentElement } = document
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    }
    const previousHtmlOverflow = documentElement.style.overflow

    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    documentElement.style.overflow = 'hidden'

    return () => {
      body.style.overflow = previousBodyStyles.overflow
      body.style.position = previousBodyStyles.position
      body.style.top = previousBodyStyles.top
      body.style.width = previousBodyStyles.width
      documentElement.style.overflow = previousHtmlOverflow
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="application-list-filters-drawer-backdrop"
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[2147483646] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        data-testid="application-list-filters-drawer"
        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-[2147483647] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        aria-hidden={!isOpen}
      >
        {/* Drawer Header */}
        <div
          data-testid="application-list-filters-drawer-header"
          className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-slate-100"
        >
          <div className="flex items-center gap-2 text-slate-800">
            <Filter
              className="h-5 w-5 text-blue-600"
              data-testid="application-list-filters-drawer-icon"
              aria-hidden
            />
            <h2
              data-testid="application-list-filters-drawer-title"
              className="text-lg font-bold"
            >
              Filters
            </h2>
          </div>
          <button
            type="button"
            data-testid="application-list-filters-drawer-close-button"
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Drawer Content (Scrollable) */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            data-testid="application-list-filters-drawer-content"
            className="flex-1 overflow-y-auto overflow-x-hidden px-6 pt-6 pb-20 space-y-6"
          >
          {/* -- 1. Employer Search (Conditional) -- */}
          {showEmployerSearch && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-employer-search-field"
            >
              <FilterLabel field="employer-search">Advanced</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.employer_search)}
                data-testid="application-list-filters-employer-search-select-wrap"
              >
                <SearchableSelect
                  options={EMPLOYER_SEARCH_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                  value={filters.employer_search ? String(filters.employer_search) : ''}
                  onChange={(value) =>
                    onFiltersChange({
                      employer_search: (Array.isArray(value) ? value[0] : value) as ApplicationFilters['employer_search'],
                    })
                  }
                  placeholder="Select Option"
                  triggerDataTestId="application-list-filters-employer-search-select"
                />
              </div>
            </div>
          )}

          {/* -- 2. Applicant Status -- */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-applicant-status-field"
          >
            <FilterLabel field="applicant-status">Applicant Status</FilterLabel>
            <div
              className={filterSelectWrapClass(filters.applicantStatus)}
              data-testid="application-list-filters-applicant-status-select-wrap"
            >
              <SearchableSelect
                options={APPLICATION_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                value={filters.applicantStatus || []}
                onChange={(value) => {
                  const statuses = Array.isArray(value) ? value : (value ? [value] : [])
                  onFiltersChange({
                    applicantStatus: statuses.length > 0 ? statuses : undefined,
                    progress_status: undefined, // Clear dependents
                  })
                }}
                placeholder="Select Status"
                multiple
                selectedChipsLayout="flex-wrap"
                triggerDataTestId="application-list-filters-applicant-status-select"
              />
            </div>
          </div>

          {/* -- 3. Progress Status -- */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-progress-status-field"
          >
            <FilterLabel field="progress-status">Progress Status</FilterLabel>
            <div
              className={filterSelectWrapClass(filters.progress_status)}
              data-testid="application-list-filters-progress-status-select-wrap"
            >
              <SearchableSelect
                options={progressStatusOptions}
                value={filters.progress_status || []}
                onChange={(value) => {
                  const statuses = Array.isArray(value) ? value : (value ? [value] : [])
                  onFiltersChange({
                    progress_status: statuses.length > 0 ? statuses : undefined,
                  })
                }}
                placeholder={(!filters.applicantStatus || filters.applicantStatus.length === 0) ? "Select Applicant Status First" : "Select Progress"}
                multiple
                selectedChipsLayout="flex-wrap"
                triggerDataTestId="application-list-filters-progress-status-select"
              />
            </div>
          </div>

          {/* -- 4. Past Customer -- */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-past-customer-field"
          >
            <FilterLabel field="past-customer">Past Customer</FilterLabel>
            <div
              className={filterSelectWrapClass(filters.past_customer)}
              data-testid="application-list-filters-past-customer-select-wrap"
            >
              <SearchableSelect
                options={PAST_CUSTOMER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                value={filters.past_customer ? String(filters.past_customer) : ''}
                onChange={(value) =>
                  onFiltersChange({
                    past_customer: (Array.isArray(value) ? value[0] : value) as ApplicationFilters['past_customer'],
                  })
                }
                placeholder="Select Option"
                triggerDataTestId="application-list-filters-past-customer-select"
              />
            </div>
          </div>

          {/* -- 5. Company -- (role-locked roles see it read-only, matching Angular control-nonclickable) */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-company-field"
          >
            <FilterLabel field="company">Company</FilterLabel>
            <div
              className={filterSelectWrapClass(lockedCompanyId ?? filters.company_id)}
              data-testid="application-list-filters-company-select-wrap"
            >
              <SearchableSelect
                options={
                  lockedCompanyId
                    ? [{ value: lockedCompanyId, label: lockedCompanyName || lockedCompanyId }]
                    : companies
                }
                value={lockedCompanyId ?? (filters.company_id ? String(filters.company_id) : '')}
                onChange={(value) => {
                  const companyId = Array.isArray(value) ? value[0] : value
                  onFiltersChange({
                    company_id: companyId ? String(companyId) : undefined,
                    property_id: undefined, // Clear dependents
                  }, {
                    company_name: companyId ? companies.find(c => c.value === companyId)?.label : undefined
                  })
                  setProperties([])
                }}
                placeholder="Select Company"
                searchPlaceholder="Search By Company Name"
                onSearch={searchCompany}
                immediateSearchOnPaste
                onPasteSearch={(text) => loadCompanies(text)}
                onOpen={() => loadCompanies('')}
                loading={loadingCompanies}
                disabled={!!lockedCompanyId}
                triggerDataTestId="application-list-filters-company-select"
              />
            </div>
          </div>

          {/* -- 6. Property -- (role-locked roles see it read-only, matching Angular control-nonclickable) */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-property-field"
          >
            <FilterLabel field="property">Property</FilterLabel>
            <div
              className={filterSelectWrapClass(lockedPropertyId ?? filters.property_id)}
              data-testid="application-list-filters-property-select-wrap"
            >
              <SearchableSelect
                options={
                  lockedPropertyId
                    ? [{ value: lockedPropertyId, label: lockedPropertyName || lockedPropertyId }]
                    : properties
                }
                value={lockedPropertyId ?? (filters.property_id ? String(filters.property_id) : '')}
                onChange={(value) => {
                  const propertyId = Array.isArray(value) ? value[0] : value
                  onFiltersChange({
                    property_id: propertyId ? String(propertyId) : undefined
                  }, {
                    property_name: propertyId ? properties.find(p => p.value === propertyId)?.label : undefined
                  })
                }}
                placeholder="Select Property"
                searchPlaceholder="Search By Property Name, City, State"
                onSearch={searchProperty}
                immediateSearchOnPaste
                onPasteSearch={(text) =>
                  loadProperties(text, filters.company_id || lockedCompanyId)
                }
                onOpen={() => {
                  if (filters.company_id || lockedCompanyId) {
                    loadProperties('', filters.company_id || lockedCompanyId)
                  }
                }}
                loading={loadingProperties}
                disabled={!!lockedPropertyId}
                triggerDataTestId="application-list-filters-property-select"
              />
            </div>
          </div>

          {/* -- Product (policy) -- */}
          {showProductFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-product-field"
            >
              <FilterLabel field="product">Product</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.product_id)}
                data-testid="application-list-filters-product-select-wrap"
              >
                <SearchableSelect
                  options={productOptions}
                  value={(filters.product_id || []).map(String)}
                  onChange={(value) => {
                    const ids = Array.isArray(value) ? value : value ? [value] : []
                    const names = ids
                      .map(
                        (id) =>
                          productOptions.find((p) => String(p.value) === String(id))?.label
                      )
                      .filter(Boolean)
                    onFiltersChange(
                      {
                        product_id:
                          ids.length > 0 ? ids.map((id) => String(id)) : undefined,
                      },
                      {
                        product_name: names.length ? names.join(', ') : undefined,
                      }
                    )
                  }}
                  placeholder="All Products"
                  multiple
                  selectedChipsLayout="flex-wrap"
                  searchPlaceholder="Search products"
                  onOpen={() => {
                    if (productOptions.length === 0 && !loadingProducts) {
                      void (async () => {
                        setLoadingProducts(true)
                        try {
                          const rows = await getActiveProductOptions()
                          setProductOptions(
                            rows.map((p) => ({
                              value: String(p.product_id),
                              label: p.name || String(p.product_id),
                            }))
                          )
                        } finally {
                          setLoadingProducts(false)
                        }
                      })()
                    }
                  }}
                  loading={loadingProducts}
                  triggerDataTestId="application-list-filters-product-select"
                />
              </div>
            </div>
          )}

          {/* -- Model (policy) -- */}
          {showModelFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-model-field"
            >
              <FilterLabel field="model">Model</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.model_id)}
                data-testid="application-list-filters-model-select-wrap"
              >
                <SearchableSelect
                  options={modelOptions}
                  value={(filters.model_id || []).map(String)}
                  onChange={(value) => {
                    const ids = Array.isArray(value) ? value : value ? [value] : []
                    const names = ids
                      .map(
                        (id) =>
                          modelOptions.find((m) => String(m.value) === String(id))?.label
                      )
                      .filter(Boolean)
                    onFiltersChange(
                      {
                        model_id:
                          ids.length > 0 ? ids.map((id) => String(id)) : undefined,
                      },
                      {
                        model_name: names.length ? names.join(', ') : undefined,
                      }
                    )
                  }}
                  placeholder="All Models"
                  multiple
                  selectedChipsLayout="flex-wrap"
                  searchPlaceholder="Search models"
                  onOpen={() => {
                    if (modelOptions.length === 0 && !loadingModels) {
                      void (async () => {
                        setLoadingModels(true)
                        try {
                          const rows = await getActiveModelOptions()
                          setModelOptions(
                            rows.map((m) => ({
                              value: String(m.model_id),
                              label: m.model_name || String(m.model_id),
                            }))
                          )
                        } finally {
                          setLoadingModels(false)
                        }
                      })()
                    }
                  }}
                  loading={loadingModels}
                  triggerDataTestId="application-list-filters-model-select"
                />
              </div>
            </div>
          )}

          {/* -- Application Screening Approach (policy) -- */}
          {showApplicationScreeningApproachFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-application-screening-approach-field"
            >
              <FilterLabel field="application-screening-approach">Application Screening Approach</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.application_screening_approach)}
                data-testid="application-list-filters-application-screening-approach-input-wrap"
              >
                <input
                  type="text"
                  value={applicationScreeningApproachDraft}
                  onChange={(event) => setApplicationScreeningApproachDraft(event.target.value)}
                  placeholder="Search Application Screening Approach"
                  data-testid="application-list-filters-application-screening-approach-input"
                  className={`w-full min-h-[48px] rounded-xl border border-slate-200 px-4 text-sm shadow-none transition-colors placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                    isFilterValueSelected(filters.application_screening_approach)
                      ? 'bg-slate-50'
                      : 'bg-white'
                  }`}
                />
              </div>
            </div>
          )}

          {/* -- Applicant Screening Approach (policy) -- */}
          {showApplicantScreeningApproachFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-screening-approach-field"
            >
              <FilterLabel field="screening-approach">Applicant Screening Approach</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.applicant_screening_approach)}
                data-testid="application-list-filters-screening-approach-input-wrap"
              >
                <input
                  type="text"
                  value={screeningApproachDraft}
                  onChange={(event) => setScreeningApproachDraft(event.target.value)}
                  placeholder="Search Applicant Screening Approach"
                  data-testid="application-list-filters-screening-approach-input"
                  className={`w-full min-h-[48px] rounded-xl border border-slate-200 px-4 text-sm shadow-none transition-colors placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                    isFilterValueSelected(filters.applicant_screening_approach)
                      ? 'bg-slate-50'
                      : 'bg-white'
                  }`}
                />
              </div>
            </div>
          )}

          {/* -- Application Risk Rating (policy) -- */}
          {showApplicationRiskRatingFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-risk-rating-field"
            >
              <FilterLabel field="risk-rating">Application Risk Rating</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.application_risk_rating)}
                data-testid="application-list-filters-risk-rating-select-wrap"
              >
                <SearchableSelect
                  options={riskRatingOptions}
                  value={filters.application_risk_rating || []}
                  onChange={(value) => {
                    const vals = Array.isArray(value) ? value : value ? [value] : []
                    onFiltersChange({
                      application_risk_rating: vals.length > 0 ? vals.map(String) : undefined,
                    })
                  }}
                  placeholder="All Application Risk Ratings"
                  multiple
                  selectedChipsLayout="flex-wrap"
                  searchPlaceholder="Search application risk ratings"
                  loading={loadingRiskLabels}
                  triggerDataTestId="application-list-filters-risk-rating-select"
                />
              </div>
            </div>
          )}

          {/* -- Applicant Risk Rating (policy) -- */}
          {showApplicantRiskRatingFilter && (
            <div
              className="space-y-2"
              data-testid="application-list-filters-applicant-risk-rating-field"
            >
              <FilterLabel field="applicant-risk-rating">Applicant Risk Rating</FilterLabel>
              <div
                className={filterSelectWrapClass(filters.applicant_risk_rating)}
                data-testid="application-list-filters-applicant-risk-rating-select-wrap"
              >
                <SearchableSelect
                  options={riskRatingOptions}
                  value={filters.applicant_risk_rating || []}
                  onChange={(value) => {
                    const vals = Array.isArray(value) ? value : value ? [value] : []
                    onFiltersChange({
                      applicant_risk_rating: vals.length > 0 ? vals.map(String) : undefined,
                    })
                  }}
                  placeholder="All Applicant Risk Ratings"
                  multiple
                  selectedChipsLayout="flex-wrap"
                  searchPlaceholder="Search applicant risk ratings"
                  loading={loadingRiskLabels}
                  triggerDataTestId="application-list-filters-applicant-risk-rating-select"
                />
              </div>
            </div>
          )}

          {/* -- 7. Date Range -- */}
          <div
            className="space-y-2"
            data-testid="application-list-filters-date-range-field"
          >
            <FilterLabel field="date-range">Date Range</FilterLabel>
            <div
              className={filterSelectWrapClass(
                filters.fromDt || filters.toDt,
                'min-w-0',
              )}
              data-testid="application-list-filters-date-range-picker-wrap"
            >
              <AdvancedDateRangePicker
                startDate={(() => {
                  if (!filters.fromDt) return null;
                  const [d, m, y] = filters.fromDt.split('-').map(Number);
                  return new Date(y, m-1, d);
                })()}
                endDate={(() => {
                  if (!filters.toDt) return null;
                  const [d, m, y] = filters.toDt.split('-').map(Number);
                  return new Date(y, m-1, d);
                })()}
                onChange={(start, end) => handleDateChange({ start, end })}
                placeholder="Select Date Range"
                customPresets={ANGULAR_COMPANY_STATS_PRESETS}
                displayRangeFormat="angularMmddyyyy"
                monthsShown={2}
                presetsOnTop
                stackMonthsVertically
                dropdownPanelClassName="w-full min-w-0 max-w-full overflow-x-hidden"
                triggerClassName={
                  filters.fromDt || filters.toDt
                    ? 'min-h-[48px] rounded-xl border-slate-200 bg-slate-50 shadow-none hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-100'
                    : 'min-h-[48px] rounded-xl border-slate-200 bg-white shadow-none hover:border-slate-300 hover:bg-white focus-visible:ring-2 focus-visible:ring-blue-100'
                }
              />
            </div>
          </div>
          </div>
        </div>

        {/* Drawer Footer */}
        <div
          data-testid="application-list-filters-drawer-footer"
          className="shrink-0 p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-4"
        >
          <button
            type="button"
            data-testid="application-list-filters-drawer-reset-button"
            onClick={onReset}
            className="filter-drawer-reset-all"
          >
            Reset All
          </button>
          <button
            type="button"
            data-testid="application-list-filters-drawer-done-button"
            onClick={onClose}
            className="flex-1 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-semibold py-2.5 rounded-lg shadow-md shadow-[rgba(21,151,255,0.22)] transition-all text-sm"
          >
            Done
          </button>
        </div>
      </aside>
    </>
  )
}
